import React, { useState, useEffect } from "react";

import { Lock } from "lucide-react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { logo } from "../../Assests/Images";
import Button from "../../Components/Button";
import CommonInput from "../../Components/Input";
import { useToast } from "../../Components/Toast";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import { fetchPublicKey, resetPassword } from "../../Store/Reducers/AuthSlice";
import { encryptWithPublicKey } from "../../Utils/Helper/clientEncryption";

type PasswordFlow = "reset_password" | "set_password";

const FLOW_COPY = {
  reset_password: {
    title: "Reset Password",
    subtitle: "Please enter your new password",
    new_password: "New Password",
    confirm_password: "Confirm Password",
    enter_new_password: "Enter new password",
    enter_confirm_password: "Confirm new password",
    submit: "Reset Password",
    back_to_login: "Back to Login",
    token_required:
      "Reset token is missing. Please use the link from your email.",
    password_required: "Password is required.",
    password_min_length: "Password must be at least 8 characters long.",
    confirm_password_required: "Please confirm your password.",
    passwords_not_match: "Passwords do not match.",
    success: "Password has been reset successfully. Redirecting to login...",
  },
  set_password: {
    title: "Set Password",
    subtitle: "Please create a password for your account",
    new_password: "New Password",
    confirm_password: "Confirm Password",
    enter_new_password: "Enter new password",
    enter_confirm_password: "Confirm new password",
    submit: "Set Password",
    back_to_login: "Back to Login",
    token_required:
      "Set password token is missing. Please use the link from your email.",
    password_required: "Password is required.",
    password_min_length: "Password must be at least 8 characters long.",
    confirm_password_required: "Please confirm your password.",
    passwords_not_match: "Passwords do not match.",
    success: "Password has been set successfully. Redirecting to login...",
  },
} as const;

const ResetPassword: React.FC = () => {
  const dispatch = useAppDispatch();
  const { publicKey, resetPasswordLoading } = useAppSelector(
    state => state.authentication
  );
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const toast = useToast();

  const flow: PasswordFlow = pathname.startsWith("/set-password")
    ? "set_password"
    : "reset_password";
  const copy = FLOW_COPY[flow];

  const [formData, setFormData] = useState({
    newPassword: "",
    confirmPassword: "",
  });

  const [error, setError] = useState({
    newPassword: "",
    confirmPassword: "",
  });

  const [token, setToken] = useState<string>("");

  useEffect(() => {
    const tokenParam = searchParams.get("token");
    if (!tokenParam) {
      toast.error(copy.token_required);
      navigate("/login");
      return;
    }
    setToken(tokenParam);
  }, [searchParams, navigate, toast, copy.token_required]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError({
      ...error,
      [e.target.name]: "",
    });
  };

  const validateForm = () => {
    let isValid = true;
    const newError = { newPassword: "", confirmPassword: "" };

    if (!formData.newPassword) {
      newError.newPassword = copy.password_required;
      isValid = false;
    } else if (formData.newPassword.length < 8) {
      newError.newPassword = copy.password_min_length;
      isValid = false;
    }

    if (!formData.confirmPassword) {
      newError.confirmPassword = copy.confirm_password_required;
      isValid = false;
    } else if (formData.newPassword !== formData.confirmPassword) {
      newError.confirmPassword = copy.passwords_not_match;
      isValid = false;
    }

    setError(newError);
    return isValid;
  };

  const handleSubmit = async () => {
    if (!validateForm() || !token) return;

    let key = publicKey;
    if (!key) {
      const response = await dispatch(fetchPublicKey());
      if (response.meta.requestStatus === "fulfilled") {
        key = (response.payload as string) || null;
      }
    }

    if (!key) {
      toast.error("Unable to fetch encryption key. Please try again.");
      return;
    }

    let encryptedPassword = "";
    try {
      encryptedPassword = await encryptWithPublicKey(key, formData.newPassword);
    } catch (encryptionError) {
      toast.error("Password encryption failed. Please try again.");
      return;
    }

    const result = await dispatch(
      resetPassword({
        token,
        new_password: encryptedPassword,
      })
    );

    if (result.meta.requestStatus === "fulfilled") {
      toast.success(copy.success);
      setTimeout(() => {
        navigate("/login");
      }, 2000);
    } else if (result.meta.requestStatus === "rejected") {
      const errorMessage =
        (result.payload as string) ||
        "Failed to reset password. Please try again.";
      toast.error(errorMessage);
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center p-8 overflow-hidden bg-gradient-to-br from-[#f5ebff] via-[#f9fcfe] to-white md:p-4">
      <div className="relative z-10 w-full max-w-[480px]">
        <div className="bg-white rounded-3xl shadow-[0_20px_60px_rgba(0,0,0,0.1),0_0_0_1px_rgba(0,0,0,0.05)] p-12 backdrop-blur-[10px] transition-all duration-300 ease-in-out md:p-8 md:rounded-[20px] md:shadow-[0_10px_40px_rgba(0,0,0,0.1)] sm:p-6">
          <div className="text-center mb-10 md:mb-8">
            <div className="mb-4 flex justify-center">
              <img
                src={logo}
                alt=""
                style={{ height: 36, width: 36 }}
                className="rounded-md object-cover"
              />
            </div>
            <h2 className="text-3xl font-bold text-[#1c1f26] mb-2 tracking-[-0.3px] md:text-2xl sm:text-[1.375rem]">
              {copy.title}
            </h2>
            <p className="text-base font-normal text-[#727272] m-0 leading-6 md:text-[0.9375rem]">
              {copy.subtitle}
            </p>
          </div>

          <div>
            <div>
              <div className="mb-4 last:mb-8 md:last:mb-7">
                <CommonInput
                  name="newPassword"
                  label={copy.new_password}
                  type="password"
                  placeholder={copy.enter_new_password}
                  value={formData.newPassword}
                  onChange={handleChange}
                  onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleSubmit();
                    }
                  }}
                  className="mb-8 w-full"
                  error={error.newPassword}
                  required
                />
              </div>

              <div className="mb-4 last:mb-8 md:last:mb-7">
                <CommonInput
                  name="confirmPassword"
                  label={copy.confirm_password}
                  type="password"
                  placeholder={copy.enter_confirm_password}
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleSubmit();
                    }
                  }}
                  className="mb-8 w-full"
                  error={error.confirmPassword}
                  required
                />
              </div>

              <div className="mt-2 flex flex-col gap-3 items-center">
                <Button
                  loading={resetPasswordLoading}
                  onClick={handleSubmit}
                  icon={<Lock size={16} className="text-white" />}
                  className="w-full md:h-12 md:text-[0.9375rem]"
                >
                  {copy.submit}
                </Button>
                <button
                  type="button"
                  onClick={() => navigate("/login")}
                  className="bg-transparent border-none text-[#974ae4] text-sm font-medium cursor-pointer py-2 px-2 no-underline transition-colors duration-200 hover:text-[#7a3bb5] hover:underline focus:outline-2 focus:outline-[#974ae4] focus:outline-offset-2 focus:rounded"
                >
                  {copy.back_to_login}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
